import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { setOptimizationSettings } from "../../scheduleSlice";
import { Schedule } from "../../../../models";
import { DumpsterSize, dumpsterSizeToText } from "../../../../models/Order";
import "./OptimizeSettingsModal.scss";

interface OptimizeSettingsModalProps {
  isOpen: boolean;
  schedule: Schedule | undefined;
  onClose: () => void;
}

export function OptimizeSettingsModal({
  isOpen,
  schedule,
  onClose,
}: OptimizeSettingsModalProps) {
  const dispatch = useAppDispatch();

  const storageYards = useAppSelector((state) =>
    state.dumpYards.templates.filter((t) => t.isStorage)
  );

  const savedSettings = useAppSelector((state) =>
    schedule ? state.schedules.optimizationSettings[schedule.id] : undefined
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [startingDumpsterSize, setStartingDumpsterSize] = useState<
    DumpsterSize | ""
  >("");
  const [startingDumpsterFull, setStartingDumpsterFull] = useState(false);

  React.useEffect(() => {
    if (isOpen && schedule) {
      if (savedSettings?.storageYardIds?.length) {
        setSelectedIds(savedSettings.storageYardIds);
      } else {
        setSelectedIds(storageYards.map((s) => s.id));
      }
      setStartingDumpsterSize(savedSettings?.startingDumpsterSize ?? "");
      setStartingDumpsterFull(savedSettings?.startingDumpsterFull ?? false);
    }
  }, [isOpen, schedule?.id]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedIds(storageYards.map((s) => s.id));
  };

  const handleSelectNone = () => {
    setSelectedIds([]);
  };

  const handleSave = () => {
    if (!schedule) return;

    dispatch(
      setOptimizationSettings({
        scheduleId: schedule.id,
        settings: {
          storageYardIds: selectedIds,
          startingDumpsterSize: startingDumpsterSize || undefined,
          startingDumpsterFull: startingDumpsterSize ? startingDumpsterFull : undefined,
        },
      })
    );
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSave();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="optimize-settings-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <div className="header-content">
            <h3>Optimization Settings</h3>
          </div>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <div className="label-row">
              <label>Starting Dumpster</label>
            </div>
            <select
              className="form-select"
              value={startingDumpsterSize}
              onChange={(e) => {
                setStartingDumpsterSize(e.target.value as DumpsterSize | "");
                if (!e.target.value) setStartingDumpsterFull(false);
              }}
            >
              <option value="">No dumpster (empty)</option>
              {Object.values(DumpsterSize).map((size) => (
                <option key={size} value={size}>
                  {dumpsterSizeToText[size]}
                </option>
              ))}
            </select>
            {startingDumpsterSize && (
              <label className="checkbox-item starting-dumpster-full">
                <input
                  type="checkbox"
                  checked={startingDumpsterFull}
                  onChange={(e) => setStartingDumpsterFull(e.target.checked)}
                />
                <span>Starting dumpster is full</span>
              </label>
            )}
          </div>

          <div className="form-group">
            <div className="label-row">
              <label>Storage Yards</label>
              <div className="select-actions">
                <button
                  type="button"
                  className="link-button"
                  onClick={handleSelectAll}
                >
                  Select All
                </button>
                <span className="separator">|</span>
                <button
                  type="button"
                  className="link-button"
                  onClick={handleSelectNone}
                >
                  Select None
                </button>
              </div>
            </div>
            <div className="storage-yards-list">
              {storageYards.map((yard) => (
                <label key={yard.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(yard.id)}
                    onChange={() => handleToggle(yard.id)}
                  />
                  <span className="yard-name">{yard.name}</span>
                  <span className="yard-address">{yard.address}</span>
                </label>
              ))}
              {storageYards.length === 0 && (
                <div className="no-yards">No storage yards available</div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="right-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
